import { mirrorKey, peekRecordingRow } from './recordingRowCache';
import {
  acceptsChunkStatus,
  aggregateRecordingProgress,
  isSettled,
  isUploadCompleteFrom,
  type ChunkKey,
  type ChunkStatusEntry,
  type PersistedStatus,
  type RecordingKey,
  type RecordingProgress,
} from './types';
import { isFenceClosed } from './writeFence';

// Chunk statuses for this page's recordings, so completion is decidable
// without IndexedDB. Gated on the row mirror: a recovery-drained recording has
// history this page never saw, so it falls through to the database.
const chunks = new Map<string, Map<number, PersistedStatus>>();

const isMirrored = (key: RecordingKey): boolean => peekRecordingRow(key) !== undefined;

// Recordings whose session this page saw hard-deleted. A deletion and lost
// audio both leave abandoned chunks, so only this tells them apart. Not gated
// on the row mirror: recovery can hit the 404 too.
const deletedSessionRecordings = new Set<string>();

export const rememberChunkStatus = (key: ChunkKey, status: PersistedStatus): void => {
  // Belt and braces with the fence-close cache reset: no status may outlive
  // the fence, even if a write races the close.
  if (isFenceClosed()) return;
  if (!isMirrored(key)) return;
  const id = mirrorKey(key);
  const existing = chunks.get(id) ?? new Map<number, PersistedStatus>();
  if (!acceptsChunkStatus(existing.get(key.index), status)) return;
  existing.set(key.index, status);
  chunks.set(id, existing);
};

/** Terminal sweep for a hard-deleted session; mirrors `markRecordingAbandoned`. */
export const rememberRecordingAbandoned = (key: RecordingKey): void => {
  deletedSessionRecordings.add(mirrorKey(key));
  const existing = chunks.get(mirrorKey(key));
  if (!existing) return;
  existing.forEach((status, index) => {
    if (!isSettled(status)) existing.set(index, 'abandoned');
  });
};

/**
 * What this page knows about one chunk; `undefined` when it never saw it. The
 * heal reads it to plant truthful rows for indexes the store never took.
 */
export const peekChunkStatus = (key: ChunkKey): PersistedStatus | undefined =>
  isMirrored(key) ? chunks.get(mirrorKey(key))?.get(key.index) : undefined;

/** `undefined` when this page did not capture the recording — read the database. */
const entriesOf = (key: RecordingKey): ChunkStatusEntry[] =>
  Array.from(chunks.get(mirrorKey(key)) ?? [], ([index, status]) => ({ index, status }));

export const peekRecordingProgress = (
  key: RecordingKey
): RecordingProgress | undefined => {
  if (!isMirrored(key)) return undefined;
  return aggregateRecordingProgress(entriesOf(key));
};

/**
 * `undefined` when this page cannot decide — read the database. A boundary or
 * an index the mirror never saw is missing knowledge, not incompleteness:
 * answering `false` there would stall completion for the rest of the load.
 */
export const peekRecordingUploadComplete = (key: RecordingKey): boolean | undefined => {
  const row = peekRecordingRow(key);
  if (!row || row.lastChunkIndex === null) return undefined;
  const entries = entriesOf(key);
  const known = new Set(entries.map((entry) => entry.index));
  for (let index = 0; index <= row.lastChunkIndex; index++) {
    if (!known.has(index)) return undefined;
  }
  return isUploadCompleteFrom(row.lastChunkIndex, entries);
};

/** Whether this page saw the recording's session hard-deleted (a session 404). */
export const wasRecordingSessionDeleted = (key: RecordingKey): boolean =>
  deletedSessionRecordings.has(mirrorKey(key));

export const forgetRecordingChunks = (key: RecordingKey): void => {
  chunks.delete(mirrorKey(key));
  deletedSessionRecordings.delete(mirrorKey(key));
};

export const resetRecordingProgressMirror = (): void => {
  chunks.clear();
  deletedSessionRecordings.clear();
};
